import React, { useEffect, useMemo, useState } from 'react'
import { keyframes } from '@emotion/react'
import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  type StackProps,
  Text,
  Textarea,
  Tooltip,
  useClipboard,
  useColorModeValue,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import { FaBus, FaCalendarAlt, FaCheck, FaCopy, FaEuroSign, FaGift, FaMapMarkerAlt, FaSearchPlus } from 'react-icons/fa'
import { MdClose } from 'react-icons/md'
import type { IconType } from 'react-icons'
import { useForm, type RegisterOptions } from 'react-hook-form'
import Swal from 'sweetalert2'
import {
  type FirebaseResponse,
  getNumberOfSignUpsChristmasDinner2026,
  sendChristmasDinner2026SignUp
} from '../../services/firebaseService'
import type { ChristmasDinner2026FormData } from '../../models/ChristmasDinner2026FormData'
import ChoiceCard from '../choice-card/ChoiceCard'
import SendingOverlay from '../sending-overlay/SendingOverlay'
import config from '../../config/config'
import { formatGalicianDate } from '../../lib/formatGalicianDate'
import { dietaryOptions } from '../../lib/dietaryOptions'
import { priceFor } from '../../lib/dinnerPrice'

type FormValues = Omit<ChristmasDinner2026FormData, 'createdAt' | 'needsBus'> & {
  needsBus?: boolean
}

type TextField = 'name' | 'lastName' | 'email' | 'phone'

// Quita espazos, puntos e guións; admite prefixo internacional (+34, 0034...)
const normalizePhone = (value: string): string => value.replace(/[\s.-]/g, '')
const PHONE_PATTERN = /^(\+|00)?\d{9,15}$/

const POSTER_ALT = 'Cartel da Cea de Nadal 2026'

// Cores tomadas do cartel: vermello Nadal, ouro e pergamiño
const GOLD = '#b8862b'
const SCRIPT_FONT = "'Dancing Script', cursive"
// Franxa de bastón de caramelo para o bordo superior da tarxeta
const CANDY_STRIPE = 'repeating-linear-gradient(-45deg, #c53030 0 12px, #fff 12px 24px, #2f855a 24px 36px, #fff 36px 48px)'

const fall = keyframes`
  from { transform: translate3d(0, -10vh, 0) rotate(0deg); }
  to { transform: translate3d(var(--drift), 110vh, 0) rotate(360deg); }
`

// Copos que caen detrás do formulario; desactívanse se o sistema pide reducir animacións
const Snowfall = (): JSX.Element => {
  const flakes = useMemo(() => Array.from({ length: 28 }, (_, i) => ({
    left: `${(i * 37) % 100}%`,
    size: `${10 + (i * 7) % 14}px`,
    duration: `${12 + (i * 5) % 14}s`,
    delay: `-${(i * 3) % 20}s`,
    drift: `${((i * 13) % 60) - 30}px`,
    opacity: 0.25 + ((i * 11) % 40) / 100
  })), [])
  return (
    <Box position="fixed" inset={0} pointerEvents="none" overflow="hidden" zIndex={0} aria-hidden
         sx={{ '@media (prefers-reduced-motion: reduce)': { display: 'none' } }}>
      {flakes.map((flake, i) => (
        <Box key={i} as="span" position="absolute" top={0} left={flake.left} color={GOLD} fontSize={flake.size}
             opacity={flake.opacity} animation={`${fall} ${flake.duration} linear ${flake.delay} infinite`}
             sx={{ '--drift': flake.drift }}>
          ❄
        </Box>
      ))}
    </Box>
  )
}

// Stack pásalle o espazado ao separador mediante props, así que hai que reenvialas
const OrnamentDivider = (props: StackProps): JSX.Element => (
  <HStack {...props} border="none" spacing={3} color={GOLD} aria-hidden>
    <Box flex={1} h="1px" bgGradient={`linear(to-r, transparent, ${GOLD})`} opacity={0.5}/>
    <Text fontSize="sm" lineHeight={1}>✦ ❄ ✦</Text>
    <Box flex={1} h="1px" bgGradient={`linear(to-l, transparent, ${GOLD})`} opacity={0.5}/>
  </HStack>
)

const FormSection = ({ step, title, children }: { step: number, title: string, children: React.ReactNode }): JSX.Element => (
  <Box as="section">
    <HStack spacing={3} mb={4}>
      <Flex
        w={8} h={8} flexShrink={0} align="center" justify="center"
        borderRadius="full" bg="red.600" color="white" fontSize="sm" fontWeight="bold"
        border="2px solid" borderColor={GOLD} boxShadow={`0 0 0 2px #fff, 0 0 0 3px ${GOLD}`}
      >
        {step}
      </Flex>
      <Heading as="h2" size="sm" textTransform="uppercase" letterSpacing="wide" color="red.700">{title}</Heading>
    </HStack>
    {children}
  </Box>
)

// En móbil é unha liña compacta dentro da tarxeta de resumo; en escritorio, unha pílula
const InfoPill = ({ icon, href, children }: { icon: IconType, href?: string, children: React.ReactNode }): JSX.Element => {
  const bg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('#e3c27a', 'gray.600')
  const pill = (
    <HStack spacing={2} align="start" px={{ base: 0, lg: 3 }} py={{ base: 0, lg: 1.5 }} bg={{ base: 'transparent', lg: bg }}
            borderWidth={{ base: 0, lg: '1px' }} borderStyle="solid" borderColor={borderColor} borderRadius="full"
            fontSize={{ base: 'xs', sm: 'sm' }} lineHeight="short"
            _hover={href != null ? { borderColor: 'red.400' } : undefined}>
      <Icon as={icon} color="red.600" mt="0.15em" flexShrink={0}/>
      <Text textDecoration={href != null ? 'underline' : undefined}>{children}</Text>
    </HStack>
  )
  return href != null ? <Link href={href} isExternal _hover={{ textDecoration: 'none' }}>{pill}</Link> : pill
}

const ChristmasDinner2026Form: React.FC = () => {
  const [isSending, setIsSending] = useState(false)
  // Empeza a true para non amosar o formulario un instante antes de saber se hai prazas
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(true)
  const [isFull, setIsFull] = useState(false)
  const poster = useDisclosure()
  const { hasCopied, onCopy } = useClipboard(config.IBAN.replace(/\s/g, ''))

  const pageBg = useColorModeValue('linear(to-b, #fbf3e0, #f5e6c4)', 'linear(to-b, gray.900, #2a1414)')
  const cardBg = useColorModeValue('white', 'gray.700')
  const mutedColor = useColorModeValue('gray.600', 'gray.300')
  const paymentBg = useColorModeValue('green.50', 'gray.600')

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    watch
  } = useForm<FormValues>({ defaultValues: { dietaryRestrictions: [], dietaryNotes: '' } })

  const isMinor = watch('isMinor')
  const needsBus = watch('needsBus')
  const dietaryRestrictions = watch('dietaryRestrictions')

  // O detalle é obrigatorio se marcou unha opción que o require (alerxia, outra)
  const dietaryDetailRequired = (dietaryRestrictions ?? [])
    .some(id => dietaryOptions.find(option => option.id === id)?.requiresDetail === true)

  // Rexistramos o campo de escolla sen input propio; o valor chega vía setValue
  useEffect(() => {
    register('needsBus', {
      validate: value => typeof value === 'boolean' || 'Tes que indicar se precisas bus'
    })
  }, [register])

  useEffect(() => {
    const previousTitle = document.title
    document.title = 'Cea de Nadal 2026 · Lechuzas Celestes'
    return () => { document.title = previousTitle }
  }, [])

  useEffect(() => {
    getNumberOfSignUpsChristmasDinner2026()
      .then(count => { setIsFull(count >= config.LIMIT_CHRISTMAS_DINNER_2026) })
      .catch((error: unknown) => { console.error('Error checking availability:', error) })
      .finally(() => { setIsCheckingAvailability(false) })
  }, [])

  const selectBus = (value: boolean): void => {
    setValue('needsBus', value, { shouldValidate: true })
  }

  const showAlert = async (r: FirebaseResponse<string>): Promise<void> => {
    if (!r.success) {
      await Swal.fire({
        title: 'Ups!',
        text: 'Algo foi mal, por favor, tenta de novo máis tarde.',
        icon: 'error',
        confirmButtonText: 'Pechar'
      })
      return
    }
    await Swal.fire({
      title: 'Grazas!',
      text: 'A túa inscrición foi enviada correctamente!',
      icon: 'success',
      confirmButtonText: 'Pechar'
    })
  }

  const submitForm = handleSubmit(async (values: FormValues) => {
    setIsSending(true)
    try {
      const data: ChristmasDinner2026FormData = {
        name: values.name.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        phone: normalizePhone(values.phone),
        isMinor: values.isMinor,
        minorConsent: values.isMinor && values.minorConsent,
        dietaryRestrictions: values.dietaryRestrictions ?? [],
        dietaryNotes: values.dietaryNotes.trim(),
        needsBus: values.needsBus === true,
        createdAt: null
      }

      const r = await sendChristmasDinner2026SignUp(data)
      // Hai que pechar o overlay antes do alert, ou SweetAlert quedaría detrás del
      setIsSending(false)
      await showAlert(r)

      if (r.success) {
        reset()
      }
    } finally {
      setIsSending(false)
    }
  })

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    void submitForm()
  }

  const renderTextField = (
    field: TextField,
    label: string,
    rules: RegisterOptions<FormValues, TextField>,
    inputProps: React.ComponentProps<typeof Input> = {}
  ): JSX.Element => (
    <FormControl isInvalid={errors[field] != null}>
      <FormLabel>{label}</FormLabel>
      <Input type="text" placeholder={label} {...inputProps} {...register(field, rules)}/>
      <FormErrorMessage>{errors[field]?.message}</FormErrorMessage>
    </FormControl>
  )

  const renderPersonalSection = (): JSX.Element => (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
      {renderTextField('name', 'Nome', {
        required: 'Tes que indicar un nome',
        minLength: { value: 2, message: 'O nome debe ter polo menos 2 caracteres' }
      }, { autoComplete: 'given-name' })}
      {renderTextField('lastName', 'Apelidos', {
        required: 'Tes que indicar os apelidos',
        minLength: { value: 2, message: 'Os apelidos deben ter polo menos 2 caracteres' }
      }, { autoComplete: 'family-name' })}
      {renderTextField('email', 'E-mail', {
        required: 'Tes que indicar un e-mail',
        pattern: { value: /\S+@\S+\.\S+/, message: 'Tes que indicar un e-mail válido' }
      }, { type: 'email', autoComplete: 'email', inputMode: 'email' })}
      {renderTextField('phone', 'Teléfono', {
        required: 'Tes que indicar un teléfono',
        validate: value => PHONE_PATTERN.test(normalizePhone(value)) || 'Tes que indicar un teléfono válido'
      }, { type: 'tel', autoComplete: 'tel', inputMode: 'tel' })}
    </SimpleGrid>
  )

  const renderAgeSection = (): JSX.Element => (
    <Stack spacing={3}>
      <Text fontSize="sm" color={mutedColor}>
        Non se admiten menores de 16 anos.
      </Text>
      <Checkbox {...register('isMinor')}>Son menor de 18 anos (teño 16 ou 17)</Checkbox>
      {isMinor && (
        <FormControl isInvalid={errors.minorConsent != null}>
          <Box bg="orange.50" p={3} borderRadius="md" border="1px solid" borderColor="orange.200">
            <Checkbox
              alignItems="flex-start"
              {...register('minorConsent', {
                validate: value => !getValues('isMinor') || value ||
                  'Tes que aceptar o consentimento para poder inscribirte'
              })}
            >
              <Text fontSize="sm" color="orange.900">
                Declaro que son consciente de que Lechuzas Celestes non se fai responsable de min durante a
                cea nin nos desprazamentos de ida e volta, e asumo a miña propia responsabilidade.
              </Text>
            </Checkbox>
            <Text fontSize="sm" color="orange.900" mt={3}>
              Nas próximas semanas poñerémonos en contacto contigo para facilitarche o formulario que deberá
              asinar o teu pai, nai ou titor/a legal.
            </Text>
          </Box>
          <FormErrorMessage>{errors.minorConsent?.message}</FormErrorMessage>
        </FormControl>
      )}
    </Stack>
  )

  const renderDietarySection = (): JSX.Element => (
    <FormControl isInvalid={errors.dietaryNotes != null}>
      <Text fontSize="sm" color={mutedColor} mb={3}>
        Marca o que corresponda. Se non tes ningunha restrición, deixa isto en branco.
      </Text>
      <Stack direction={{ base: 'column', sm: 'row' }} spacing={{ base: 2, sm: 6 }} mb={3}>
        {dietaryOptions.map(option => (
          <Checkbox key={option.id} value={option.id} {...register('dietaryRestrictions')}>
            {option.label}
          </Checkbox>
        ))}
      </Stack>
      <Textarea
        rows={2}
        placeholder={dietaryDetailRequired ? 'Detalle (obrigatorio)' : 'Detalle (opcional)'}
        {...register('dietaryNotes', {
          validate: value => !dietaryDetailRequired || value.trim() !== '' ||
            'Indícanos o detalle da túa alerxia ou restrición'
        })}
      />
      <FormErrorMessage>{errors.dietaryNotes?.message}</FormErrorMessage>
    </FormControl>
  )

  const renderTransportSection = (): JSX.Element => (
    <Stack spacing={4}>
      <FormControl isInvalid={errors.needsBus != null}>
        <FormLabel>Precisas bus gratuíto?</FormLabel>
        <Stack direction="row" spacing={{ base: 3, md: 4 }} role="radiogroup">
          <ChoiceCard label="Si, preciso bus" icon={FaBus} colorScheme="green"
                      selected={needsBus === true} onSelect={() => { selectBus(true) }}/>
          <ChoiceCard label="Non preciso bus" icon={MdClose} colorScheme="red"
                      selected={needsBus === false} onSelect={() => { selectBus(false) }}/>
        </Stack>
        <FormErrorMessage>{errors.needsBus?.message}</FormErrorMessage>
        <Text fontSize="sm" color={mutedColor} mt={3}>
          O bus gratuíto sairá dende a cafetaría The Movie, en Balaídos. Confirmaremos a hora máis adiante.
        </Text>
      </FormControl>

      <Box bg="blue.50" p={4} borderRadius="lg" border="1px solid" borderColor="blue.200">
        <Text fontSize="sm" color="blue.800">
          Se queres pasar a noite, o hotel ofrécenos un <b>{config.HOTEL_DISCOUNT_PERCENT}% de desconto</b>.
          Podes reservar a túa habitación directamente no hotel:{' '}
          <Link href={config.HOTEL_URL} isExternal color="blue.600" fontWeight="bold">
            Talaso Atlántico
          </Link>
          {' '}co código promocional <b>{config.HOTEL_DISCOUNT_CODE}</b>.
        </Text>
      </Box>
    </Stack>
  )

  const renderPaymentSection = (): JSX.Element => (
    <Box bg={paymentBg} p={4} borderRadius="lg" border="1px solid" borderColor="green.200">
      <VStack align="stretch" spacing={3}>
        <Text fontSize="sm">Pago por adiantado mediante transferencia.</Text>
        <Box>
          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={mutedColor}>IBAN</Text>
          <HStack spacing={2}>
            <Text fontFamily="mono" fontSize={{ base: 'sm', sm: 'md' }} fontWeight="semibold">{config.IBAN}</Text>
            <Tooltip label={hasCopied ? 'Copiado!' : 'Copiar IBAN'} closeOnClick={false}>
              <IconButton
                aria-label="Copiar IBAN" size="xs" variant="ghost" colorScheme="green"
                icon={<Icon as={hasCopied ? FaCheck : FaCopy}/>} onClick={onCopy}
              />
            </Tooltip>
          </HStack>
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={mutedColor}>Asunto</Text>
          <Text fontSize="sm">O teu nome e apelidos</Text>
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={mutedColor}>Importe</Text>
          <Text fontSize="sm">
            <Text as="span" fontSize="lg" fontWeight="bold">{priceFor(isMinor)} €</Text>{' '}
            ({config.CHRISTMAS_DINNER_2026_PRICE_ADULT} € adultos ·{' '}
            {config.CHRISTMAS_DINNER_2026_PRICE_MINOR} € menores de 18 anos)
          </Text>
        </Box>
      </VStack>
    </Box>
  )

  const renderForm = (): JSX.Element => (
    <form onSubmit={handleFormSubmit} noValidate>
      <Stack spacing={8} divider={<OrnamentDivider/>}>
        <FormSection step={1} title="Os teus datos">{renderPersonalSection()}</FormSection>
        <FormSection step={2} title="Idade">{renderAgeSection()}</FormSection>
        <FormSection step={3} title="Alimentación especial">{renderDietarySection()}</FormSection>
        <FormSection step={4} title="Transporte e aloxamento">{renderTransportSection()}</FormSection>
        <FormSection step={5} title="Pagamento">{renderPaymentSection()}</FormSection>
        <Stack spacing={3}>
          <Button type="submit" size="lg" w="100%" bg="red.600" color="white" _hover={{ bg: 'red.700' }}
                  leftIcon={<Icon as={FaGift}/>} boxShadow={`0 4px 0 ${GOLD}`} isDisabled={isSending}>
            Inscribirse
          </Button>
          <Text fontSize="sm" color={mutedColor} textAlign="center">
            Inscricións abertas ata esgotar o aforo ou ata o{' '}
            {formatGalicianDate(config.FORM_CHRISTMAS_DINNER_2026_DEACTIVATION_DATE)}.
          </Text>
        </Stack>
      </Stack>
    </form>
  )

  const renderFormContent = (): JSX.Element => {
    if (isCheckingAvailability) {
      return <Flex align="center" justify="center" gap={2} py={8}><Spinner/><Text>Comprobando dispoñibilidade...</Text></Flex>
    }
    if (isFull) {
      return <Text>Sentímolo, pero xa se cubriron todas as prazas para a cea de Nadal 2026.</Text>
    }
    return renderForm()
  }

  return (
    <Box bgGradient={pageBg} minH="100vh" pt="70px" position="relative">
      <Snowfall/>
      <SendingOverlay isOpen={isSending}/>
      <Container position="relative" zIndex={1} maxW="6xl" py={{ base: 4, md: 10 }} px={{ base: 4, md: 6 }} textAlign="left">
        <Grid templateColumns={{ base: 'minmax(0, 1fr)', lg: '360px minmax(0, 1fr)' }} gap={{ base: 6, lg: 10 }}
              alignItems="start">
          {/* En escritorio o cartel queda fixo ao lado; en móbil vese en miniatura e ábrese ao tocalo */}
          <GridItem display={{ base: 'none', lg: 'block' }} position="sticky" top="90px">
            <Image
              src={config.CHRISTMAS_DINNER_2026_BANNER}
              alt={POSTER_ALT}
              w="100%"
              maxH="calc(100vh - 120px)"
              objectFit="contain"
              borderRadius="lg"
              boxShadow="lg"
              cursor="zoom-in"
              onClick={poster.onOpen}
            />
          </GridItem>

          <GridItem>
            <Stack spacing={{ base: 3, lg: 4 }} mb={{ base: 4, lg: 6 }}>
              <Image src="/img/logo.png" alt="Le-Chuzas Celestes" h={{ base: '34px', md: '44px' }} w="auto"
                     alignSelf="flex-start"/>
              <Heading as="h1" fontWeight={700} lineHeight="100%">
                <Text as="span" display="block" fontFamily={SCRIPT_FONT} color="red.600"
                      fontSize={{ base: '4xl', md: '6xl' }}>
                  Cea de Nadal 2026
                </Text>
                <Text as="span" display="block" color={GOLD} fontSize={{ base: 'sm', md: 'md' }}
                      textTransform="uppercase" letterSpacing="0.2em" mt={1}>
                  ✦ Lechuzas Celestes ✦
                </Text>
              </Heading>
              <HStack align="stretch" spacing={3} bg={{ base: cardBg, lg: 'transparent' }} p={{ base: 3, lg: 0 }}
                      rounded="xl" boxShadow={{ base: 'md', lg: 'none' }}>
                <Box as="button" type="button" aria-label="Ver cartel" onClick={poster.onOpen}
                     display={{ base: 'block', lg: 'none' }} position="relative" flexShrink={0} w={{ base: '84px', sm: '110px' }}>
                  <Image src={config.CHRISTMAS_DINNER_2026_BANNER} alt="" w="100%" h="100%" objectFit="cover" borderRadius="md"/>
                  <HStack position="absolute" bottom={1} left={1} right={1} justify="center" spacing={1}
                          bg="blackAlpha.700" color="white" fontSize="2xs" borderRadius="sm" py={0.5}>
                    <Icon as={FaSearchPlus}/><Text>Ver cartel</Text>
                  </HStack>
                </Box>
                <Flex direction={{ base: 'column', lg: 'row' }} wrap="wrap" justify={{ base: 'center', lg: 'flex-start' }}
                      gap={{ base: 2, lg: 2 }} minW={0}>
                  <InfoPill icon={FaCalendarAlt}>{formatGalicianDate(config.CHRISTMAS_DINNER_2026_DATE)}</InfoPill>
                  <InfoPill icon={FaMapMarkerAlt} href={config.HOTEL_MAPS_URL}>{config.CHRISTMAS_DINNER_2026_DETAILS}</InfoPill>
                  <InfoPill icon={FaEuroSign}>
                    {config.CHRISTMAS_DINNER_2026_PRICE_ADULT} € adultos · {config.CHRISTMAS_DINNER_2026_PRICE_MINOR} € menores
                  </InfoPill>
                  <InfoPill icon={FaBus}>Bus gratuíto dende Vigo</InfoPill>
                </Flex>
              </HStack>
            </Stack>

            <Box rounded="xl" bg={cardBg} boxShadow="lg" overflow="hidden" border="1px solid" borderColor="#e3c27a">
              <Box h="8px" bgImage={CANDY_STRIPE} aria-hidden/>
              <Box p={{ base: 5, md: 8 }}>
                {renderFormContent()}
              </Box>
            </Box>
          </GridItem>
        </Grid>
      </Container>

      <Modal isOpen={poster.isOpen} onClose={poster.onClose} size="xl" isCentered>
        <ModalOverlay/>
        <ModalContent bg="transparent" boxShadow="none" mx={4}>
          <ModalCloseButton color="white" bg="blackAlpha.600" _hover={{ bg: 'blackAlpha.700' }} zIndex={1}/>
          <ModalBody p={0}>
            <Image src={config.CHRISTMAS_DINNER_2026_BANNER} alt={POSTER_ALT} w="100%" maxH="90vh"
                   objectFit="contain" borderRadius="lg"/>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default ChristmasDinner2026Form
